import {
  Autocomplete,
  AutocompleteRenderGetTagProps,
  Avatar,
  Box,
  Checkbox,
  Chip,
  TextField,
} from '@mui/material';
import type { DashboardSmallUser } from 'api/models/DashboardSmallUser';
import { getInitials } from 'utilities/getInitials';
import { UserAutocompleteProps } from './filters.props';
import { modelFiltersStyles } from './ModelFilters.styles';

export const UserAutocomplete = ({
  options,
  value,
  onChange,
  onInputChange,
  inputValue,
  loading,
  label,
  placeholder,
  zoomLevel,
  textFieldSx = modelFiltersStyles.autocompleteTextField,
  disabled = false,
  maxVisibleTags = 2,
  showFullNames = false,
}: UserAutocompleteProps) => {
  const getOptionLabel = (option: DashboardSmallUser) => {
    const fullName = [option.first_name, option.last_name].filter(Boolean).join(' ');
    return fullName || option.email || '';
  };

  const renderTags = (tagValue: DashboardSmallUser[], getTagProps: AutocompleteRenderGetTagProps) => {
    if (tagValue.length === 0) return null;

    if (showFullNames) {
      return tagValue.map((option, index) => {
        const { key, ...chipProps } = getTagProps({ index });
        return (
          <Chip
            key={key}
            {...chipProps}
            label={getOptionLabel(option)}
            size="small"
            sx={{ ...modelFiltersStyles.chip, maxWidth: 'none', fontSize: '0.75rem', '& .MuiChip-label': { paddingRight: '6px' } }}
          />
        );
      });
    }

    const visibleItems = tagValue.slice(0, maxVisibleTags);
    const remainingCount = tagValue.length - maxVisibleTags;

    return (
      <Box sx={{ ...modelFiltersStyles.getTagsContainer(tagValue.length), maxWidth: `${maxVisibleTags * 28 + 40}px` }}>
        {visibleItems.map(option => (
          <Avatar key={option.id} sx={modelFiltersStyles.avatar}>
            {getInitials({ name: `${option.first_name} ${option.last_name}` })}
          </Avatar>
        ))}
        {remainingCount > 0 && (
          <Chip label={`+${remainingCount}`} size="small" sx={modelFiltersStyles.chip} />
        )}
      </Box>
    );
  };

  return (
    <Autocomplete
      size="small"
      multiple
      options={options}
      getOptionLabel={getOptionLabel}
      slotProps={{
        popper: {
          sx: modelFiltersStyles.getAutocompletePopper(zoomLevel),
        },
      }}
      disableCloseOnSelect
      disabled={disabled}
      value={value}
      onChange={onChange}
      onInputChange={onInputChange}
      inputValue={inputValue}
      loading={loading}
      limitTags={-1}
      renderInput={params => (
        <TextField
          {...params}
          size="small"
          label={label}
          variant="outlined"
          placeholder={value.length === 0 ? placeholder : ''}
          sx={textFieldSx}
        />
      )}
      renderTags={renderTags}
      renderOption={(props, option, { selected }) => {
        const { key, ...optionProps } = props;
        return (
          <li key={option.id} {...optionProps} style={modelFiltersStyles.optionListItem}>
            <Checkbox style={modelFiltersStyles.optionCheckbox} checked={selected} />
            {[option.first_name, option.last_name].join(' ') || option.email || ''}
          </li>
        );
      }}
      sx={[
        modelFiltersStyles.autocompleteRoot,
        showFullNames && {
          '& .MuiAutocomplete-inputRoot': {
            flexWrap: 'wrap !important',
          },
        },
      ]}
      isOptionEqualToValue={(option, val) => option.id === val.id}
    />
  );
};
